import { useEffect, useRef } from 'react'
import userApi from '../../../services/api/user'

// Retry helper function with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const isNetworkError =
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Network request failed') ||
        error.name === 'TypeError' ||
        error.name === 'NetworkError'

      if (!isNetworkError || attempt === maxRetries - 1) {
        throw error
      }

      const delay = baseDelay * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

/**
 * Hook to batch load notes for multiple subsidiaries
 * Loads notes in batches of specified size with delays between batches
 * 
 * @param {Array} subsidiaryIds - Array of { subsidiaryId, kind } objects
 * @param {Object} options - Configuration options
 * @param {number} options.batchSize - Number of notes to load per batch (default: 20)
 * @param {number} options.batchDelay - Delay between batches in ms (default: 300)
 * @param {number} options.initialDelay - Initial delay before starting to load in ms (default: 500)
 * @param {Function} options.onNoteLoaded - Callback when a note is loaded (subsidiaryId, kind, data)
 */
const useBatchNoteLoader = (
  subsidiaryIds = [],
  { batchSize = 20, batchDelay = 300, initialDelay = 500, onNoteLoaded } = {}
) => {
  const loadingRef = useRef(false)
  const processedRef = useRef(new Set())
  const notesCacheRef = useRef(new Map())

  useEffect(() => {
    if (!Array.isArray(subsidiaryIds) || subsidiaryIds.length === 0) {
      return
    }

    // Reset processed set when subsidiaryIds change significantly
    const currentKeys = new Set(subsidiaryIds.map(({ subsidiaryId, kind }) => `${subsidiaryId}-${kind}`))
    const previousKeys = processedRef.current
    // Only reset if there are many new keys
    if (currentKeys.size > previousKeys.size * 1.5) {
      processedRef.current = new Set()
      notesCacheRef.current = new Map()
    }

    const loadNote = async (subsidiaryId, kind) => {
      const key = `${subsidiaryId}-${kind}`
      
      // Skip if already processed
      if (processedRef.current.has(key)) {
        return
      }

      // Check cache first
      if (notesCacheRef.current.has(key)) {
        const cachedData = notesCacheRef.current.get(key)
        if (onNoteLoaded) {
          onNoteLoaded(subsidiaryId, kind, cachedData)
        }
        processedRef.current.add(key)
        return
      }

      try {
        const noteKey = `${kind}-${subsidiaryId}`
        const response = await retryWithBackoff(() =>
          userApi.retrieveNote({
            Key: noteKey,
            Kind: kind,
            SubsidiaryId: subsidiaryId
          })
        )
        
        const noteData = response?.data || ''
        notesCacheRef.current.set(key, noteData)
        processedRef.current.add(key)
        
        if (onNoteLoaded) {
          onNoteLoaded(subsidiaryId, kind, noteData)
        }
      } catch (err) {
        // Silently fail for batch loading - individual notes can retry on focus
        console.warn(`Failed to load note for ${subsidiaryId}:`, err)
        processedRef.current.add(key) // Mark as processed to avoid retrying
      }
    }

    const loadBatch = async (batch) => {
      // Load all notes in the batch in parallel
      await Promise.allSettled(
        batch.map(({ subsidiaryId, kind }) => loadNote(subsidiaryId, kind))
      )
    }

    const startBatchLoading = async () => {
      if (loadingRef.current) return
      loadingRef.current = true

      // Wait for initial delay
      await new Promise(resolve => setTimeout(resolve, initialDelay))

      // Process in batches
      for (let i = 0; i < subsidiaryIds.length; i += batchSize) {
        const batch = subsidiaryIds.slice(i, i + batchSize)
        await loadBatch(batch)

        // Wait before next batch (except for the last batch)
        if (i + batchSize < subsidiaryIds.length) {
          await new Promise(resolve => setTimeout(resolve, batchDelay))
        }
      }

      loadingRef.current = false
    }

    startBatchLoading()

    // Cleanup function
    return () => {
      loadingRef.current = false
    }
  }, [subsidiaryIds, batchSize, batchDelay, initialDelay, onNoteLoaded])

  return notesCacheRef.current
}

export default useBatchNoteLoader

